import React from "react";
import { toast } from "react-toastify";
import { useState, useEffect, useContext } from "react";
import "./AccountProfile.scss";
import http from "../../services/http-commons.js";
import { SetAllContext } from "../../services/AllContext.js";
import { useNavigate } from "react-router-dom";

const AccountProfile = () => {
  const { userID } = useContext(SetAllContext);
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState(null);
  const [error, setError] = useState(null);

  const [editableFields, setEditableFields] = useState({
    mobile_number: "",
    age: "",
    gender: "",
  });

  const [isEditing, setIsEditing] = useState(false);

  // ============== USEEFFECTS ================

  useEffect(() => {
    setLoading(true);
    setError(null);
    setErrorCode(null);

    http.USERHTTP.get(`/user/me`)
      .then((response) => {
        setUserData(response.data);

        setEditableFields({
          mobile_number: response.data.mobile_number || "",
          age: response.data.age || "",
          gender: response.data.gender || "",
        });
      })
      .catch((error) => {
        console.log("ERRORRRRR", error);

        if (error.response) {
          setErrorCode(error.response.status);

          if (error.response.status === 401) {
            setError("Unauthorized. Please log in to continue.");
          } else if (error.response.status === 500) {
            setError(
              "Something went wrong on the server. Please try again later.",
            );
          } else {
            setError(
              error.response.data?.detail ||
                "An unexpected error occurred. Please try again.",
            );
          }
        } else if (error.request) {
          setError("No response from server. Check your connection.");
        } else {
          setError(`Error: ${error.message}`);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }, [userID]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setEditableFields((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = () => {
    const updateData = {
      user_id: userID,
      ...editableFields,
    };

    const filteredUpdate = Object.fromEntries(
      Object.entries(updateData).filter(([_, v]) => v !== ""),
    );

    const updateCustomer = http.USERHTTP.patch(
      "/customer-update",
      filteredUpdate,
    );

    toast.promise(
      updateCustomer,
      {
        pending: "Updating account...",
        success: "Account successfully updated! 🎉",
        error: "Unable to update account 😞",
      },
      {
        autoClose: 3000,
      },
    );

    updateCustomer
      .then(() => {
        setIsEditing(false);
      })
      .catch((error) => {
        toast.error(
          `${
            error.response?.data?.detail
              ? error.response.data.detail
              : "An error occurred"
          }`,
          {
            autoClose: 3000,
          },
        );
      });
  };

  // =================== LOADING ===================

  if (loading) {
    return (
      <div className="login-required-container">
        <div className="account-loader"></div>
        <p>Loading your account...</p>
      </div>
    );
  }

  // =================== LOGIN REQUIRED ===================

  if (errorCode === 401) {
    return (
      <div className="login-required-container">
        <div className="login-required-card">
          <div className="login-icon">🔐</div>

          <h2>Login Required</h2>

          <p>
            Please log in to your account to continue and access this page.
          </p>

          <button
            className="login-required-btn"
            onClick={() => navigate("/auth/login")}
          >
            Log In
          </button>
        </div>
      </div>
    );
  }

  // =================== OTHER ERRORS ===================

  if (error) {
    return (
      <div className="login-required-container">
        <div className="login-required-card">
          <div className="login-icon">⚠️</div>

          <h2>Something went wrong</h2>

          <p>{error}</p>

          <button
            className="login-required-btn"
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // =================== ACCOUNT ===================

  return (
    <div className="account-informataion">
      <div>
        <div className="d-flex justify-content-between align-items-center">
          <h4>Account Information</h4>

          {!isEditing ? (
            <div className="d-flex mb-3">
              <button
                className="btn active ms-auto p-2"
                onClick={() => setIsEditing(true)}
              >
                Edit Profile
              </button>
            </div>
          ) : null}
        </div>

        <hr />

        <div className="mb-3">
          <label htmlFor="firstName" className="form-label">
            First Name
          </label>

          <input
            type="text"
            value={userData?.first_name || ""}
            disabled
            className="form-control form-control-lg"
            id="firstName"
          />
        </div>

        <div className="mb-3">
          <label htmlFor="lastName" className="form-label">
            Last Name
          </label>

          <input
            type="text"
            value={userData?.last_name || ""}
            disabled
            className="form-control form-control-lg"
            id="lastName"
          />
        </div>

        <div className="mb-3">
          <label htmlFor="email" className="form-label">
            Email Address
          </label>

          <input
            type="email"
            value={userData?.email || ""}
            disabled
            className="form-control form-control-lg"
            id="email"
          />
        </div>

        {/* Editable Fields */}

        <div className="mb-3">
          <label htmlFor="mobile_number" className="form-label">
            Mobile Number
          </label>

          <input
            type="text"
            name="mobile_number"
            value={editableFields.mobile_number}
            onChange={handleInputChange}
            disabled={!isEditing}
            className="form-control form-control-lg"
            id="mobile_number"
          />
        </div>

        <div className="mb-3">
          <label htmlFor="age" className="form-label">
            Age
          </label>

          <input
            type="number"
            name="age"
            value={editableFields.age}
            onChange={handleInputChange}
            disabled={!isEditing}
            className="form-control form-control-lg"
            id="age"
          />
        </div>

        <div className="mb-3">
          <label htmlFor="gender" className="form-label">
            Gender
          </label>

          <select
            name="gender"
            value={editableFields.gender}
            onChange={handleInputChange}
            disabled={!isEditing}
            className="form-control form-control-lg"
            id="gender"
            style={{ fontSize: "12px" }}
          >
            <option value="">Select Gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
          </select>
        </div>

        {/* Save / Cancel */}

        <div>
          {isEditing ? (
            <div className="d-flex w-100 gap-2">
              <button
                className="btn btn-primary w-50"
                onClick={handleSave}
              >
                Save
              </button>

              <button
                className="btn btn-secondary w-50"
                onClick={() => setIsEditing(false)}
              >
                Cancel
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AccountProfile;